import { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Collapse, HoverCard, UnstyledButton } from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';

import ChevronDown from '@/assets/icons/content/chevron-down.svg';

import { useMercuryPermissions } from '@/hooks/useMercuryPermissions';
import { useMercuryPages } from '@/hooks/useMercuryPages';

import type { RoutePathDefinition } from '@/types/routes';
import { mapRouteswithChildren } from './mapRouteswithChildren';
import { useSidebarSubmenuAccordion } from './SidebarSubmenuAccordionContext';
import { isRouteUnderSubmenu } from './submenuRouteMatch';

import classes from './Navbar.module.css';

type SubMenuItem = RoutePathDefinition & { nestedSubmenu?: boolean };

const SubMenuChild = ({
  item,
  sideBarExpanded,
}: {
  item: SubMenuItem;
  sideBarExpanded: boolean;
}) => {
  const { icon: NavIcon, name, path, children } = item;
  const isNested = item.nestedSubmenu === true;
  const matchesDesktop = useMediaQuery('(min-width: 62em)', undefined, {
    getInitialValueInEffect: false,
  });
  const { pathname } = useLocation();
  const { openSubmenuPath, toggleSubmenu } = useSidebarSubmenuAccordion();
  const { getPageInfo } = useMercuryPages();
  const { hasAccess } = useMercuryPermissions();

  const routeActive = useMemo(() => isRouteUnderSubmenu(item, pathname), [item, pathname]);

  const [nestedOpen, setNestedOpen] = useState(() => routeActive);
  useEffect(() => {
    if (!isNested) return;
    if (routeActive) setNestedOpen(true);
  }, [isNested, routeActive]);

  const accordionOpen = isNested ? nestedOpen : openSubmenuPath === path;

  const page = item.getPageInfo ? getPageInfo(item.page) : undefined;
  const canAccess = page?.permissions ? hasAccess(page.permissions) : true;

  if (item.hideOnNoAccess && !canAccess) return null;

  const mapSubmenuChildren = (args: { showTooltip: boolean; expandedSidebar: boolean }) =>
    children
      ?.map((child: RoutePathDefinition) => {
        if (child.isSubMenu && child.children) {
          return (
            <SubMenuChild
              key={`submenu-${child.path}`}
              item={{ ...child, nestedSubmenu: true }}
              sideBarExpanded={args.expandedSidebar}
            />
          );
        }
        return mapRouteswithChildren({
          child,
          sideBarExpanded: args.expandedSidebar,
          showTooltip: args.showTooltip,
        });
      })
      .filter((node) => node !== undefined);

  const mappedChildrenExpanded = mapSubmenuChildren({
    showTooltip: true,
    expandedSidebar: sideBarExpanded,
  });

  const mappedChildrenFlyout = mapSubmenuChildren({
    showTooltip: false,
    expandedSidebar: false,
  });

  if (!matchesDesktop) {
    return (
      <li className={classes.submenuRoot}>
        <div className={classes.submenuListWrapper}>
          <div className={classes.mobileSubmenuTitle}>{name}</div>
          <ul className={classes.submenuList}>{mappedChildrenExpanded}</ul>
        </div>
      </li>
    );
  }

  /* Collapsed desktop strip: hover opens flyout to the right (Figma “Moon” collapsed column).
     Use HoverCard, not Menu: Menu defaults trapFocus=true on the inner Popover and can hide /
     break the rest of the sidebar (icons “missing” with gaps). */
  if (!sideBarExpanded) {
    /* Root flyout: dock the panel to the right edge of the collapsed rail.
       Rail = 60px, AppShell.Navbar p="md" = 16px → trigger.right ≈ 44px,
       so mainAxis: 16 lands the dropdown at x ≈ 60 (flush with the rail).
       A large negative value here (previously -160, copied from a Figma
       coordinate) shifts the dropdown off the left edge of the viewport.
       Nested flyout (e.g. Catalogs inside Threat Intelligence) opens from a
       row inside the already-open parent panel, so a small positive gap
       separates parent and child panels visually. */
    const flyoutOffset = isNested ? { mainAxis: 20, crossAxis: 0 } : { mainAxis: 16, crossAxis: 0 };

    return (
      <li className={classes.submenuItem}>
        <HoverCard
          position="right-start"
          offset={flyoutOffset}
          openDelay={80}
          closeDelay={120}
          transitionProps={{
            transition: 'pop',
            duration: 180,
            timingFunction: 'cubic-bezier(0.44, 0.8, 0.71, 0.98)',
          }}
          classNames={{ dropdown: classes.collapsedFlyoutDropdown }}
          withinPortal
        >
          <HoverCard.Target>
            <UnstyledButton
              type="button"
              className={`${classes.link} ${classes.submenuCollapsedTrigger} ${routeActive ? classes.submenuCollapsedTriggerActive : ''}`}
              aria-haspopup="menu"
              aria-label={name}
            >
              <span className={classes.linkIcon}>{NavIcon && <NavIcon />}</span>
              <span className={classes.linkLabel} title={name}>
                {name}
              </span>
              <span className={classes.submenuChevron} aria-hidden>
                <ChevronDown className={classes.submenuChevronIcon} />
              </span>
            </UnstyledButton>
          </HoverCard.Target>

          <HoverCard.Dropdown>
            <div
              className={`${classes.collapsedFlyoutHeader} ${classes.submenuAccordionTriggerActive}`}
            >
              {NavIcon ? (
                <span className={classes.linkIcon}>
                  <NavIcon />
                </span>
              ) : null}
              <span className={NavIcon ? classes.linkLabel : undefined}>{name}</span>
            </div>
            <ul className={classes.collapsedFlyoutList}>{mappedChildrenFlyout}</ul>
          </HoverCard.Dropdown>
        </HoverCard>
      </li>
    );
  }

  /* Expanded desktop: in-sidebar accordion + slide animation (Figma expanded columns). */
  return (
    <li className={classes.submenuRoot}>
      <UnstyledButton
        type="button"
        className={`${classes.link} ${classes.submenuAccordionTrigger} ${routeActive ? classes.submenuAccordionTriggerActive : ''}`}
        aria-expanded={accordionOpen}
        onClick={() => (isNested ? setNestedOpen((o) => !o) : toggleSubmenu(path))}
      >
        <span className={classes.linkIcon}>{NavIcon && <NavIcon />}</span>
        <span className={classes.linkLabel} title={name}>
          {name}
        </span>
        <span
          className={`${classes.submenuChevron} ${accordionOpen ? classes.submenuChevronOpen : ''}`}
          aria-hidden
        >
          <ChevronDown className={classes.submenuChevronIcon} />
        </span>
      </UnstyledButton>

      <Collapse
        in={accordionOpen}
        transitionDuration={200}
        transitionTimingFunction="cubic-bezier(0.44, 0.8, 0.71, 0.98)"
      >
        <div className={classes.submenuAccordionPanel}>
          <ul className={classes.submenuAccordionList}>{mappedChildrenExpanded}</ul>
        </div>
      </Collapse>
    </li>
  );
};

export default SubMenuChild;
